/* oxlint-disable max-lines */
import * as React from 'react';
import { debounce } from 'throttle-debounce';
import { DropdownPopover, Input, Outside } from '@cian/ui-kit';
import { IRailway, IRoad } from '../../../types/geo';
import { MAX_SUGGESTIONS } from '../../../interfaces/villages';
import { IBuilderSuggest } from '../../../types/builder';
import { IGeo } from '../../../store/geo';
import { IRoads } from '../../../store/roads';
import { IStations } from '../../../store/stations';
import { isArrayWithItems } from '../../../utils';
import { addressSuggest, filterGeoSuggest, getBoundedBy, IGeoSuggestItem } from '../../../utils/geo';
import { Suggestions } from './Suggestions';
import { LeftAdornment } from './components/LeftAdornment';
import { RightAdornment } from './components/RightAdornment';

interface IProps {
  geo: IGeo;
  roads: IRoads;
  stations: IStations;
  textValue: string;
  roadButton: {
    label: string;
    available: boolean;
  };
  onChange(value: string): void;
  onItemSelect(suggest: IGeoSuggestItem): void;
  onRoadSelect(suggest: IRoad): void;
  onStationSelect(suggest: IRailway): void;
  onBuilderSelect(suggest: IBuilderSuggest): void;
  fetchVillageSuggestions(text: string): void;
  fetchBuilderSuggestions(text: string): void;
  filterRoads(text: string): void;
  filterStations(text: string): void;
  clearVillageSuggestions(): void;
  clearBuilderSuggestions(): void;
  showGeoPopup(): void;
  showCommutePopup(): void;
}

interface IState {
  isResultsVisible: boolean;
  isLoading: boolean;
  suggests: IGeoSuggestItem[];
  selectedVillageItemIndex: number;
}

export class GeoTextField extends React.Component<IProps, IState> {
  public state = {
    isLoading: false,
    isResultsVisible: false,
    selectedVillageItemIndex: -1,
    suggests: [],
  };

  private inputRef = React.createRef<HTMLInputElement>();
  private contentRef = React.createRef<HTMLDivElement>();

  public render() {
    const {
      roads,
      stations,
      textValue,
      roadButton,
      geo: { villageSuggestions, builderSuggestions },
      showGeoPopup,
      showCommutePopup,
    } = this.props;

    const { isLoading, isResultsVisible, suggests } = this.state;

    return (
      <Outside onOutside={this.handleBlur} insideRefs={[this.inputRef, this.contentRef]}>
        <DropdownPopover
          open={isResultsVisible}
          content={
            <div ref={this.contentRef}>
              <Suggestions
                textValue={textValue}
                isLoading={isLoading}
                isResultsVisible={isResultsVisible}
                villageSuggests={villageSuggestions}
                builderSuggests={builderSuggestions}
                roadsSuggests={roads.suggests}
                stationsDirections={stations.directions}
                stationsSuggests={stations.suggests}
                addressSuggests={suggests}
                handleRoadItemSelect={this.handleRoadItemSelect}
                handleStationItemSelect={this.handleStationItemSelect}
                handleAddressItemSelect={this.handleAddressItemSelect}
                handleBuilderItemSelect={this.handleBuilderItemSelect}
              />
            </div>
          }
        >
          <Input
            data-testid="GeoTextField"
            ref={this.inputRef}
            placeholder={this.placeholder}
            value={textValue}
            onChange={this.handleValueChange}
            onFocus={this.handleFocus}
            leftAdornment={<LeftAdornment />}
            rightAdornment={
              <RightAdornment roadButton={roadButton} onRegionClick={showGeoPopup} onRoadClick={showCommutePopup} />
            }
          />
        </DropdownPopover>
      </Outside>
    );
  }

  private get placeholder() {
    const road = this.props.roads.regionHasRoads ? ', шоссе' : '';
    const railway = this.props.stations.regionHasStations ? ', ж/д' : '';

    return `Город, адрес, район${road}${railway}`;
  }

  private handleValueChange = (_e: React.ChangeEvent<HTMLInputElement>, value: string) => {
    this.props.onChange(value);
    this.loadSuggestsDebounced(value);
  };

  private loadSuggests = async (rawValue: string) => {
    const {
      clearVillageSuggestions,
      fetchVillageSuggestions,
      fetchBuilderSuggestions,
      clearBuilderSuggestions,
      filterRoads,
      filterStations,
    } = this.props;
    const value = rawValue.trim();
    if (!value) {
      clearVillageSuggestions();
      clearBuilderSuggestions();
      filterRoads(value);
      filterStations(value);

      return this.setState({
        isResultsVisible: false,
        suggests: [],
      });
    } else if (value.toLowerCase().startsWith('кп')) {
      this.setState({ isLoading: true, suggests: [] });
      clearBuilderSuggestions();
      fetchVillageSuggestions(value);

      return this.setState({
        isLoading: false,
        isResultsVisible: this.hasVillageSuggests(),
      });
    }
    const { currentRegion } = this.props.geo;
    const boundedBy = getBoundedBy(currentRegion);
    clearVillageSuggestions();
    clearBuilderSuggestions();
    this.setState({
      isLoading: true,
      suggests: [],
    });
    Promise.all([
      addressSuggest(`Россия, ${value}`, { boundedBy, results: 15 }),
      fetchVillageSuggestions(value),
      fetchBuilderSuggestions(value),
      filterRoads(value),
      filterStations(value),
    ])
      .then(([suggests]) => {
        this.setState({
          isLoading: false,
          isResultsVisible: true,
          suggests: suggests.filter(filterGeoSuggest).slice(0, MAX_SUGGESTIONS),
        });
      })
      .catch(() => {
        this.setState({
          isLoading: false,
          isResultsVisible: this.hasAnySuggests(),
        });
      });
  };

  private loadSuggestsDebounced = debounce(250, this.loadSuggests);

  private resetSuggestions = () => {
    const { filterRoads, filterStations, clearVillageSuggestions, clearBuilderSuggestions } = this.props;

    filterRoads('');
    filterStations('');
    clearVillageSuggestions();
    clearBuilderSuggestions();

    this.setState({
      suggests: [],
    });
  };

  private handleBlur = () => {
    this.setState({
      isResultsVisible: false,
    });
  };

  private handleFocus = () => {
    this.setState({
      isResultsVisible: true,
    });
  };

  private hasVillageSuggests = () => isArrayWithItems(this.props.geo.villageSuggestions);

  private hasBuilderSuggests = () => isArrayWithItems(this.props.geo.builderSuggestions);

  private hasRoadsSuggests = () => isArrayWithItems(this.props.roads.suggests);

  private hasStationsSuggests = () => isArrayWithItems(this.props.stations.suggests);

  private hasAnySuggests = () =>
    this.hasVillageSuggests() || this.hasRoadsSuggests() || this.hasStationsSuggests() || this.hasBuilderSuggests();

  private handleAddressItemSelect = (suggest: IGeoSuggestItem) => {
    this.setState({
      isResultsVisible: false,
    });

    this.props.onChange('');
    this.props.onItemSelect(suggest);
  };

  private handleRoadItemSelect = (suggest: IRoad) => {
    this.setState({
      isResultsVisible: false,
    });

    this.props.onChange('');
    this.props.onRoadSelect(suggest);
    this.resetSuggestions();
  };

  private handleStationItemSelect = (suggest: IRailway) => {
    this.setState({
      isResultsVisible: false,
    });

    this.props.onChange('');
    this.props.onStationSelect(suggest);
    this.resetSuggestions();
  };

  private handleBuilderItemSelect = (suggest: IBuilderSuggest) => {
    this.setState({
      isResultsVisible: false,
    });

    this.props.onChange('');
    this.props.onBuilderSelect(suggest);
    this.resetSuggestions();
  };
}
